import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import reportTemplatesService from '../services/reportTemplatesService';
import DialogModal from '../components/DialogModal';
import { Plus, PencilSimple, Trash, Star } from '@phosphor-icons/react';

const PLACEHOLDERS = [
  '{{ school_name }}', '{{ student_name }}', '{{ class_name }}', '{{ academic_term }}', '{{ generated_date }}',
  '{{ attendance_display }}', '{{ class_size }}', '{{ position }}', '{{ class_average }}',
  '{{ overall_average }}', '{{ overall_grade }}', '{{ overall_description }}',
  '{{ class_teacher_remarks }}', '{{ head_teacher_remarks }}', '{{ attitude }}', '{{ conduct }}', '{{ interest }}',
  '{{ vacation_date }}', '{{ reopening_date }}', '{{ promoted_to }}',
  '{% for subject in subjects %} ... {{ subject.subject_name }}, {{ subject.class_score }}, {{ subject.exam_score }}, {{ subject.total_score }}, {{ subject.grade }} ... {% endfor %}',
];

const emptyForm = { name: '', description: '', html_content: '', is_default: false };

function ReportTemplatesPage() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [settingDefaultId, setSettingDefaultId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await reportTemplatesService.listTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load report card templates');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (template) => {
    setEditingId(template.id);
    setFormData({
      name: template.name,
      description: template.description || '',
      html_content: template.html_content,
      is_default: template.is_default,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.html_content) {
      toast.error('Name and HTML content are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await reportTemplatesService.updateTemplate(editingId, formData);
        toast.success('Template updated');
      } else {
        await reportTemplatesService.createTemplate(formData);
        toast.success('Template created');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save template');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await reportTemplatesService.deleteTemplate(deleteTarget);
      toast.success('Template deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete template');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSetDefault = async (templateId) => {
    setSettingDefaultId(templateId);
    try {
      await reportTemplatesService.setDefaultTemplate(templateId);
      toast.success('Default template updated');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to set default template');
    } finally {
      setSettingDefaultId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading report card templates...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Report Card Templates</h1>
            <p>Customize the layout report cards are rendered with. The default template is used when none is set.</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Add Template
          </button>
        </div>

        <div className="table-container">
          {templates.length === 0 ? (
            <div className="empty-state">
              <p>No custom templates yet — report cards use the default built-in layout.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Version</th>
                  <th>Default</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td>{template.name}</td>
                    <td>{template.description || '-'}</td>
                    <td>v{template.version}</td>
                    <td>{template.is_default ? 'Yes' : 'No'}</td>
                    <td className="action-buttons">
                      {!template.is_default && (
                        <button
                          className="btn-icon"
                          title="Set as default"
                          onClick={() => handleSetDefault(template.id)}
                          disabled={settingDefaultId === template.id}
                        >
                          <Star size={18} />
                        </button>
                      )}
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(template)}>
                        <PencilSimple size={18} />
                      </button>
                      <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteTarget(template.id)}>
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 920 }}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Template' : 'Add Template'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    style={{ width: 'auto', marginRight: 8 }}
                  />
                  Set as the default template (used when generating report cards)
                </label>
              </div>

              <div className="form-group">
                <label>Template Content (HTML + Jinja2) *</label>
                <textarea
                  value={formData.html_content}
                  onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                  rows={16}
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                  placeholder="<html>...</html>"
                  required
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  Available placeholders: {PLACEHOLDERS.map((p, i) => (
                    <code key={i} style={{ marginRight: 6, display: 'inline-block' }}>{p}</code>
                  ))}
                </small>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Template"
        message="This will permanently delete this report card template. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default ReportTemplatesPage;
